import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { calendrierApi } from '../api/calendrier.api'
import type {
  CreateCalendrierEtudiantRequest,
  UpdateCalendrierEtudiantRequest,
  CreateDatesPeriodeEtudiantRequest,
} from '@/shared/types/calendrier'

const CALENDRIER_KEY = ['calendrier-etudiant']
const PERIODES_KEY = ['periodes-etudiant']

// ── Calendrier ─────────────────────────────────────────────

export function useCalendriersEtudiant(etudiantId: number | undefined) {
  return useQuery({
    queryKey: [...CALENDRIER_KEY, etudiantId],
    queryFn: () => calendrierApi.getByEtudiantId(etudiantId!),
    enabled: !!etudiantId,
  })
}

export function useCreateCalendrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCalendrierEtudiantRequest) => calendrierApi.create(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...CALENDRIER_KEY, variables.etudiantId] })
      queryClient.invalidateQueries({ queryKey: [...PERIODES_KEY, variables.etudiantId] })
    },
  })
}

export function useUpdateCalendrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCalendrierEtudiantRequest }) =>
      calendrierApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALENDRIER_KEY })
    },
  })
}

export function useDeleteCalendrier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => calendrierApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALENDRIER_KEY })
      queryClient.invalidateQueries({ queryKey: PERIODES_KEY })
    },
  })
}

// ── Périodes étudiant ──────────────────────────────────────

export function usePeriodesEtudiant(etudiantId: number | undefined) {
  return useQuery({
    queryKey: [...PERIODES_KEY, etudiantId],
    queryFn: () => calendrierApi.getPeriodesByEtudiantId(etudiantId!),
    enabled: !!etudiantId,
  })
}

// ── Dates période ──────────────────────────────────────────

export function useCreateDatePeriode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateDatesPeriodeEtudiantRequest) =>
      calendrierApi.createDatePeriode(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...CALENDRIER_KEY, variables.etudiantId] })
      queryClient.invalidateQueries({ queryKey: [...PERIODES_KEY, variables.etudiantId] })
    },
  })
}

export function useDeleteDatePeriode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => calendrierApi.deleteDatePeriode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALENDRIER_KEY })
      queryClient.invalidateQueries({ queryKey: PERIODES_KEY })
    },
  })
}
